<script lang="ts">
import { formatCount } from '$lib/utils/report/calculations'
import { getStudyChartThemeColors, type RgbTriple } from '$lib/utils/report/chart-config'
import { onDestroy, onMount, tick } from 'svelte'
import StudySlideStatTileCard from '../cards/StudySlideStatTileCard.svelte'

interface Props {
  stateData: Record<string, number>
  /**
   * Use the diverging red -> slate -> blue ramp instead of the default single-hue teal
   * fade. Off by default so the other four studies keep their existing map.
   */
  divergingScale?: boolean
  topStates: [string, number][]
  chartTitle?: string
  chartSubtitle?: string
  calloutText?: string
}

let {
  stateData,
  topStates,
  divergingScale = false,
  chartTitle = 'Total Claims by State',
  chartSubtitle = 'Tap or hover a state to see exact claim volume · IMX Data',
  calloutText = '50 states tracked. Every adoption curve, every payer shift — visible as it happens.',
}: Props = $props()

/** Empty-state fill, shared by the initial render and the hover reset. */
const NO_DATA_FILL = 'rgba(255,255,255,0.06)'

/**
 * Choropleth fill for a normalised value `t` (0–1).
 *
 * Sequential (default): a single teal fading in opacity. Fine for "where is the mass", but it asks
 * the reader to judge opacity, which is unreliable through the middle of the range.
 *
 * Diverging: low → middle → high across three HUES rather than strengths of one, so mid-range
 * states separate at a glance. Stops come from the theme (`--chart-diverging-*`) because a ramp
 * tuned for the near-black slide washes out on the light export and vice versa. Interpolated in RGB
 * across two halves; `t` is clamped so a value equal to the max cannot overshoot the last stop.
 */
function rampColor(t: number, diverging: boolean, stops: DivergingStops): string {
  const clamped = Math.min(1, Math.max(0, t))
  if (!diverging) return `rgba(17,160,165,${(0.18 + clamped * 0.82).toFixed(2)})`
  const half = clamped < 0.5 ? 0 : 1
  const local = clamped < 0.5 ? clamped / 0.5 : (clamped - 0.5) / 0.5
  const from = stops[half]
  const to = stops[half + 1]
  const mix = (i: number) => Math.round(from[i] + (to[i] - from[i]) * local)
  return `rgb(${mix(0)},${mix(1)},${mix(2)})`
}

/** Dark-theme ramp, matching the `--chart-diverging-*` defaults in the presentation shell. */
const DEFAULT_DIVERGING_STOPS: DivergingStops = [
  [139, 26, 26],
  [128, 128, 128],
  [34, 211, 238],
]

/** Active theme's ramp, resolved from the presentation host. Falls back to the dark set when the
 *  chart is rendered outside a `.study-presentation` (tests, isolated stories). */
function resolveDivergingStops(el: HTMLElement | null | undefined): DivergingStops {
  const host = (el?.closest('.study-presentation') ?? el) as HTMLElement | null
  if (!host) return DEFAULT_DIVERGING_STOPS
  return getStudyChartThemeColors(host).divergingStops
}

type DivergingStops = [RgbTriple, RgbTriple, RgbTriple]

let wrap: HTMLDivElement
let hoveredStateName = $state('Tap a state')
let hoveredStateVal = $state('—')
let activeStateCode = $state<string | null>(null)
let mapError = $state<string | null>(null)

/** Cached after first successful fetch so resize rebuilds don’t re-download. */
let cachedUsTopo: { objects: { states: unknown } } | null = null

let resizeObserver: ResizeObserver | null = null
let resizeDebounce: ReturnType<typeof setTimeout> | null = null

function measureWrapWidth(): number {
  if (!wrap) return 800
  const rect = wrap.getBoundingClientRect()
  let w = Math.floor(rect.width)
  if (w < 48) w = wrap.clientWidth
  if (w < 48 && typeof window !== 'undefined') {
    w = Math.min(window.innerWidth, 1200) - 32
  }
  return Math.max(w, 48)
}

function scheduleRebuild(): void {
  if (resizeDebounce) clearTimeout(resizeDebounce)
  resizeDebounce = setTimeout(() => {
    resizeDebounce = null
    void buildMap()
  }, 100)
}

// ── FIPS code → state abbreviation ────────────────────────────────────────
const FIPS: Record<string, string> = {
  '01': 'AL',
  '02': 'AK',
  '04': 'AZ',
  '05': 'AR',
  '06': 'CA',
  '08': 'CO',
  '09': 'CT',
  '10': 'DE',
  '11': 'DC',
  '12': 'FL',
  '13': 'GA',
  '15': 'HI',
  '16': 'ID',
  '17': 'IL',
  '18': 'IN',
  '19': 'IA',
  '20': 'KS',
  '21': 'KY',
  '22': 'LA',
  '23': 'ME',
  '24': 'MD',
  '25': 'MA',
  '26': 'MI',
  '27': 'MN',
  '28': 'MS',
  '29': 'MO',
  '30': 'MT',
  '31': 'NE',
  '32': 'NV',
  '33': 'NH',
  '34': 'NJ',
  '35': 'NM',
  '36': 'NY',
  '37': 'NC',
  '38': 'ND',
  '39': 'OH',
  '40': 'OK',
  '41': 'OR',
  '42': 'PA',
  '44': 'RI',
  '45': 'SC',
  '46': 'SD',
  '47': 'TN',
  '48': 'TX',
  '49': 'UT',
  '50': 'VT',
  '51': 'VA',
  '53': 'WA',
  '54': 'WV',
  '55': 'WI',
  '56': 'WY',
  '72': 'PR',
}

const stateNames: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'D.C.',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  PR: 'Puerto Rico',
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve()
      return
    }
    const s = document.createElement('script')
    s.src = src
    s.onload = () => resolve()
    s.onerror = reject
    document.head.appendChild(s)
  })
}

async function buildMap(): Promise<void> {
  if (!wrap) return
  wrap.querySelectorAll('svg').forEach((el) => el.remove())
  mapError = null

  try {
    await loadScript('https://cdn.jsdelivr.net/npm/d3@7/dist/d3.min.js')
  } catch (e) {
    const msg = 'ChoroplethChart: failed to load d3 script'
    console.error(msg, e)
    mapError = msg
    return
  }
  try {
    await loadScript('https://cdn.jsdelivr.net/npm/topojson-client@3/dist/topojson-client.min.js')
  } catch (e) {
    const msg = 'ChoroplethChart: failed to load topojson-client script'
    console.error(msg, e)
    mapError = msg
    return
  }

  if (!cachedUsTopo) {
    try {
      const res = await fetch('https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      cachedUsTopo = (await res.json()) as { objects: { states: unknown } }
    } catch (e) {
      const msg = 'ChoroplethChart: failed to fetch states GeoJSON (us-atlas states-10m.json)'
      console.error(msg, e)
      mapError = msg
      return
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d3: any = (window as any).d3
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const topojson: any = (window as any).topojson

  const usTopo = cachedUsTopo
  const values = Object.values(stateData)
  const maxClaimsValue = values.length === 0 ? 0 : Math.max(0, ...values)

  function getStateColor(v: number): string {
    if (!v) return NO_DATA_FILL
    return rampColor(v / maxClaimsValue, divergingScale, resolveDivergingStops(wrap))
  }

  const rawWidth = measureWrapWidth()
  // "Compact" = small in either dimension. Catches phones in portrait (≤640 wide) AND
  // phones in landscape (≤500 tall) where the desktop maxHeight formula
  // (window.innerHeight − 26rem) goes negative and produces a broken render.
  const isCompactScreen =
    typeof window !== 'undefined' && (window.innerWidth <= 640 || window.innerHeight <= 500)
  const maxHeight = isCompactScreen
    ? Math.round(rawWidth * 0.65)
    : Math.max(window.innerHeight - 26 * 16, 240)
  const naturalHeight = Math.round(rawWidth * 0.62)
  const height = Math.min(naturalHeight, maxHeight)
  const width = Math.min(rawWidth, Math.round(height / 0.62))

  const states = topojson.feature(usTopo, usTopo.objects.states)
  const projection = d3.geoAlbersUsa().fitSize([width, height], states)
  const pathGen = d3.geoPath().projection(projection)

  const svgNamespace = 'http://www.w3.org/2000/svg'
  const svg = document.createElementNS(svgNamespace, 'svg')
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`)
  svg.setAttribute('id', 'us-map-svg')
  svg.setAttribute('xmlns', svgNamespace)
  /* Match projection width — not 100% of the container. When maxHeight caps height, width is
   * reduced below rawWidth; 100% would upscale the SVG and make it taller than .choro-wrap’s
   * max-height, clipping the south (e.g. Texas) under overflow:hidden. */
  svg.style.width = `${width}px`
  svg.style.maxWidth = '100%'
  svg.style.height = 'auto'
  svg.style.display = 'block'

  for (const feature of states.features) {
    const fips = String(feature.id).padStart(2, '0')
    const stateCode = FIPS[fips] ?? ''
    const pathData = pathGen(feature) as string | null
    if (!pathData) continue

    const pathEl = document.createElementNS(svgNamespace, 'path')
    pathEl.setAttribute('d', pathData)
    pathEl.setAttribute('fill', getStateColor(stateData[stateCode] ?? 0))
    pathEl.setAttribute('stroke', '#071219')
    pathEl.setAttribute('stroke-width', '0.5')
    pathEl.style.cursor = 'pointer'
    pathEl.style.transition = 'fill .15s'

    const title = document.createElementNS(svgNamespace, 'title')
    title.textContent = `${stateNames[stateCode] ?? stateCode}: ${formatCount(stateData[stateCode] ?? 0)}`
    pathEl.appendChild(title)

    function applyActiveStyle(el: SVGPathElement) {
      el.classList.add('is-active')
      el.setAttribute('fill', '#22d4dc')
    }
    function clearActiveStyle(el: SVGPathElement, code: string) {
      el.classList.remove('is-active')
      el.setAttribute('fill', getStateColor(stateData[code] ?? 0))
    }

    pathEl.addEventListener('mouseenter', () => {
      pathEl.setAttribute('fill', '#22d4dc')
      hoveredStateName = stateNames[stateCode] ?? stateCode
      hoveredStateVal = formatCount(stateData[stateCode] ?? 0)
    })
    pathEl.addEventListener('mouseleave', () => {
      if (activeStateCode !== stateCode) {
        pathEl.setAttribute('fill', getStateColor(stateData[stateCode] ?? 0))
      }
      if (activeStateCode && activeStateCode !== stateCode) {
        hoveredStateName = stateNames[activeStateCode] ?? activeStateCode
        hoveredStateVal = formatCount(stateData[activeStateCode] ?? 0)
      } else if (!activeStateCode) {
        hoveredStateName = 'Tap a state'
        hoveredStateVal = '—'
      }
    })
    pathEl.addEventListener('click', (ev) => {
      ev.stopPropagation()
      if (activeStateCode && activeStateCode !== stateCode) {
        const prev = svg.querySelector<SVGPathElement>(`path[data-state="${activeStateCode}"]`)
        if (prev) clearActiveStyle(prev, activeStateCode)
      }
      activeStateCode = activeStateCode === stateCode ? null : stateCode
      if (activeStateCode === stateCode) {
        applyActiveStyle(pathEl)
        hoveredStateName = stateNames[stateCode] ?? stateCode
        hoveredStateVal = formatCount(stateData[stateCode] ?? 0)
      } else {
        clearActiveStyle(pathEl, stateCode)
        hoveredStateName = 'Tap a state'
        hoveredStateVal = '—'
      }
    })
    pathEl.setAttribute('data-state', stateCode)

    svg.appendChild(pathEl)
  }

  wrap.appendChild(svg)
}

function handleWrapClick(): void {
  // Tap-outside-state deselect (state click handlers stopPropagation, so this only fires
  // when the click missed every <path>).
  if (!activeStateCode || !wrap) return
  const svg = wrap.querySelector<SVGElement>('#us-map-svg')
  if (!svg) return
  const prev = svg.querySelector<SVGPathElement>(`path[data-state="${activeStateCode}"]`)
  if (prev) {
    prev.classList.remove('is-active')
    const v = stateData[activeStateCode] ?? 0
    const max = Math.max(1, ...Object.values(stateData))
    prev.setAttribute(
      'fill',
      v ? rampColor(v / max, divergingScale, resolveDivergingStops(wrap)) : NO_DATA_FILL,
    )
  }
  activeStateCode = null
  hoveredStateName = 'Tap a state'
  hoveredStateVal = '—'
}

onMount(() => {
  void (async () => {
    await tick()
    await new Promise<void>((r) => requestAnimationFrame(() => r()))
    await new Promise<void>((r) => requestAnimationFrame(() => r()))
    await buildMap()
    resizeObserver = new ResizeObserver(() => scheduleRebuild())
    if (wrap) resizeObserver.observe(wrap)
  })()
})

onDestroy(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
  if (resizeDebounce) clearTimeout(resizeDebounce)
})
</script>

<div class="choro-layout">
  <div class="choro-main">
    <div class="cc choro-map-card">
      <div class="ct">{chartTitle}</div>
      <div class="cs">{chartSubtitle}</div>
      <div bind:this={wrap} class="choro-wrap" onclick={handleWrapClick} role="presentation">
        {#if mapError}
          <div
            class="choro-error"
            style="padding:2rem;text-align:center;color:var(--mu);font-size:0.85rem;"
          >
            Map could not be loaded. {mapError}
          </div>
        {/if}
      </div>
      <div style="display:flex;align-items:center;gap:.5rem;margin-top:.6rem;">
        <span style="font-size:.62rem;color:var(--mu);">Lower</span>
        <div
          style="flex:1;height:8px;border-radius:4px;background:{divergingScale
            ? 'linear-gradient(90deg,var(--chart-diverging-low),var(--chart-diverging-mid),var(--chart-diverging-high))'
            : 'linear-gradient(90deg,rgba(17,160,165,.12),rgba(17,160,165,1))'};"
        ></div>
        <span style="font-size:.62rem;color:{divergingScale ? 'rgb(34,211,238)' : 'var(--mu)'};"
          >Higher</span
        >
      </div>
    </div>
  </div>
  <div class="choro-side">
    <div class="cc choro-info-card">
      <div class="ct" style="font-size:.85rem;margin-bottom:.28rem;">{hoveredStateName}</div>
      <div class="choro-info-value">
        {hoveredStateVal}
      </div>
      <div style="font-size:.62rem;color:var(--mu);margin-top:.18rem;">total claims</div>
    </div>
    <div class="choro-tile-grid">
      {#each topStates as [state, claims], i}
        <StudySlideStatTileCard
          variant="choro"
          value={state}
          label={`${formatCount(claims)} · #${i + 1}`}
        />
      {/each}
    </div>
    <div class="hb choro-callout">
      <p style="font-size:.7rem;">{calloutText}</p>
    </div>
  </div>
</div>

<style>
.choro-layout {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}
.choro-main {
  flex: 1;
  min-width: 0;
}
.cc.choro-map-card {
  padding: 0.85rem 1rem;
}
.choro-side {
  width: 185px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.choro-info-card {
  min-height: 85px;
}
.choro-info-value {
  font-family: 'DM Serif Display', serif;
  font-size: 1.5rem;
  color: var(--g);
  line-height: 1;
}
.choro-tile-grid {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.choro-callout {
  padding: 0.8rem 0.95rem;
}
.choro-wrap {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 6px;
  max-height: calc(100vh - 26rem);
}
:global(#us-map-svg) {
  max-width: 100%;
  height: auto;
  display: block;
}
@media (min-width: 641px) {
  /* Desktop / tablet: when the height cap shrinks the SVG below the wrap width, center it
   * so the leftover space splits evenly instead of pooling on the right. */
  :global(#us-map-svg) {
    margin-left: auto;
    margin-right: auto;
  }
}
:global(#us-map-svg path) {
  stroke: #071219;
  stroke-width: 0.5;
  transition:
    fill 0.15s,
    stroke 0.15s;
  cursor: pointer;
}
:global(#us-map-svg path:hover),
:global(#us-map-svg path.is-active) {
  stroke: #22d4dc;
  stroke-width: 1.5;
}
/* Tablet / narrow: stack but keep two-column tile grid */
@media (max-width: 1024px) {
  .choro-side {
    width: 100%;
  }
  .choro-tile-grid {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .choro-tile-grid > :global(*) {
    flex: 1 1 calc(33.333% - 0.4rem);
    min-width: 110px;
  }
  .choro-layout {
    flex-direction: column;
  }
}
/* Tablet + phone — full-bleed map.
 * Note: most of the structural full-bleed (shell positioning, .cc padding stripping,
 * .choro-wrap width) is handled in StudyPresentationShell.svelte via the
 * .study-s4b-choro-shell selector with !important on the same ≤1024px breakpoint.
 * The rule below drops the card chrome so the card edge isn't visible alongside the
 * full-bleed map. */
@media (max-width: 1024px) {
  .cc.choro-map-card {
    background: none;
    border: none;
    border-radius: 0;
  }
}
/* Phone-specific layout tweaks the shell rules don't touch. */
@media (max-width: 640px) {
  .choro-info-value {
    font-size: 1.35rem;
  }
  .choro-tile-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }
  .choro-tile-grid > :global(*) {
    min-width: 0;
  }
}
</style>
